import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Armchair,
  CheckCircle2,
  MessageCircle,
  MoreHorizontal,
  Tag,
  Users,
  XCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import ReservationDetailsRow from './ReservationDetailsRow';
import {
  extractDiscountInfo,
  formatReservationMonthDay,
  getOriginMeta,
  getReservationContactPhone,
  getReservationGuestName,
  getReservationTimeValue,
  getStatusMeta,
} from './utils';

const GRID_LAYOUT_CLASS = 'grid grid-cols-[200px_1fr_150px_90px_120px_120px_90px] gap-4';

export default function ReservationListView({
  reservations = [],
  onStatusChange,
  onSelect,
  onSendFeedback,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    setCurrentPage(1);
  }, [reservations.length]);

  const totalItems = reservations.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  const currentReservations = useMemo(
    () => reservations.slice(startIndex, endIndex),
    [reservations, startIndex, endIndex],
  );

  const toggleExpand = (reservationId) => {
    setExpandedId((current) => (current === reservationId ? null : reservationId));
  };

  if (!reservations.length) {
    return (
      <div className="flex h-full flex-col items-center justify-center rounded-xl border-2 border-dashed bg-gray-50/50 py-16 text-center">
        <div className="mb-4 rounded-full bg-white p-4 shadow-sm">
          <AlertCircle className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="mb-1 text-lg font-semibold text-gray-900">Keine Reservierungen gefunden</h3>
        <p className="max-w-sm text-sm text-gray-500">
          Passe Filter oder Zeitraum an, um Reservierungen anzuzeigen.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col space-y-4">
      <div className="min-h-0 flex-1 overflow-auto rounded-lg border bg-gray-50/30">
        <div className="min-w-[1030px]">
          <div className={`${GRID_LAYOUT_CLASS} sticky top-0 z-10 border-b bg-white px-6 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500 shadow-sm`}>
            <div>Datum & Zeit</div>
            <div>Gast</div>
            <div>Quelle & Tisch</div>
            <div>Pax</div>
            <div>Status</div>
            <div>Rabatt</div>
            <div className="text-right">Aktionen</div>
          </div>

          <div className="divide-y divide-gray-100">
            {currentReservations.map((reservation) => {
              const monthDay = formatReservationMonthDay(reservation);
              const guestName = getReservationGuestName(reservation);
              const phone = getReservationContactPhone(reservation);
              const status = getStatusMeta(reservation.status);
              const origin = getOriginMeta(reservation.origin);
              const discountInfo = extractDiscountInfo(reservation);

              return (
                <div key={reservation.id} className="group bg-white transition-colors hover:bg-gray-50">
                  <div
                    className={`${GRID_LAYOUT_CLASS} cursor-pointer items-center px-6 py-4`}
                    onClick={() => {
                      toggleExpand(reservation.id);
                      onSelect?.(reservation);
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 flex-col items-center justify-center rounded-lg border border-gray-200/60 bg-gray-100/80 shadow-sm">
                        <span className="text-[10px] font-bold uppercase leading-none text-gray-500">{monthDay.month}</span>
                        <span className="mt-0.5 text-xl font-bold leading-none text-gray-900">{monthDay.day}</span>
                      </div>

                      <div className="flex flex-col items-start gap-1">
                        <div className="text-sm font-bold text-gray-900">{getReservationTimeValue(reservation)}</div>
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-bold tracking-tight text-green-700">
                          {String(reservation.reservation_date || '')}
                        </span>
                      </div>
                    </div>

                    <div className="min-w-0">
                      <div className="truncate font-bold text-gray-900">{guestName}</div>
                      {phone ? (
                        <div className="mt-0.5 truncate text-sm text-gray-500">{phone}</div>
                      ) : null}
                      {(reservation.notes || reservation.special_requests) ? (
                        <div className="mt-1 flex items-center gap-1 text-xs font-medium text-amber-600">
                          <MessageCircle className="h-3 w-3" />
                          <span className="truncate">{reservation.notes || reservation.special_requests}</span>
                        </div>
                      ) : null}
                    </div>

                    <div className="flex flex-col items-start gap-1.5">
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${origin.className}`}>
                        {origin.label}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {reservation.table_name || reservation.table?.name || 'Nicht zugewiesen'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 font-medium text-gray-600">
                      <Users className="h-4 w-4 text-gray-400" />
                      <span>{reservation.party_size || 0}</span>
                    </div>

                    <div>
                      <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${status.badgeClass}`}>
                        {status.label}
                      </span>
                    </div>

                    <div>
                      {discountInfo.hasDiscount ? (
                        <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                          <Tag className="mr-1 h-3 w-3" />-{discountInfo.percentage}%
                        </span>
                      ) : (
                        <span className="px-2 text-sm font-medium text-gray-400">-</span>
                      )}
                    </div>

                    <div className="flex items-center justify-end gap-1">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-400 hover:bg-blue-50 hover:text-blue-600"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <Armchair className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Status setzen</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={(event) => { event.stopPropagation(); onStatusChange?.(reservation.id, 'confirmed'); }}>
                            <CheckCircle2 className="mr-2 h-4 w-4 text-green-600" /> Bestaetigen
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(event) => { event.stopPropagation(); onStatusChange?.(reservation.id, 'seated'); }}>
                            <Armchair className="mr-2 h-4 w-4 text-blue-600" /> Anwesend
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(event) => { event.stopPropagation(); onStatusChange?.(reservation.id, 'completed'); }}>
                            <CheckCircle2 className="mr-2 h-4 w-4 text-slate-600" /> Abschliessen
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(event) => { event.stopPropagation(); onStatusChange?.(reservation.id, 'no_show'); }}>
                            <AlertCircle className="mr-2 h-4 w-4 text-orange-600" /> No-Show
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(event) => { event.stopPropagation(); onStatusChange?.(reservation.id, 'cancelled'); }}>
                            <XCircle className="mr-2 h-4 w-4 text-rose-600" /> Stornieren
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-400 hover:text-gray-700"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuLabel>Aktionen</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={(event) => { event.stopPropagation(); toggleExpand(reservation.id); }}>
                            Details anzeigen
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(event) => { event.stopPropagation(); onSendFeedback?.(reservation); }}>
                            Feedback-Erinnerung senden
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {expandedId === reservation.id ? (
                    <div className="border-t bg-gray-50/50">
                      <ReservationDetailsRow reservation={reservation} />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 z-20 mt-auto flex flex-col items-center justify-between gap-4 border-t bg-background px-4 py-4 sm:flex-row">
        <div className="order-2 flex items-center gap-2 text-sm text-muted-foreground sm:order-1">
          <span>Zeige</span>
          <span className="font-medium text-foreground">{startIndex + 1}</span>
          <span>bis</span>
          <span className="font-medium text-foreground">{Math.min(endIndex, totalItems)}</span>
          <span>von</span>
          <span className="font-medium text-foreground">{totalItems}</span>
          <span>Eintraegen</span>
        </div>

        <div className="order-1 flex items-center gap-4 sm:order-2">
          <div className="flex items-center gap-2">
            <span className="hidden text-sm text-muted-foreground sm:inline">Zeilen:</span>
            <Select
              value={String(itemsPerPage)}
              onValueChange={(value) => {
                setItemsPerPage(Number(value));
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={itemsPerPage} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
              &laquo;
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={currentPage === 1}>
              &lsaquo;
            </Button>
            <div className="flex min-w-[3rem] items-center justify-center text-sm font-medium">
              {currentPage} / {totalPages}
            </div>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} disabled={currentPage === totalPages}>
              &rsaquo;
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
              &raquo;
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
